/**
 * Runtime around the pure health machine (health.ts, #771): one HealthMonitor per network, fed by traffic the app
 * already makes.
 *
 * - /info: HlProvider wraps the InfoClient's fetch with wrapFetch(), so every attempt (retries included) reports
 *   ok + latency / 429 (slow) / 5xx or network error / no answer after infoTimeoutMs. Aborted requests are ignored.
 * - /exchange: postExchange() calls reportExchangeTransport() (HTTP < 500 = ok; order rejections are ok).
 * - WebSocket: attachWs() maps the sockets' combined state (open → ws_up, reconnecting → ws_down, closed → ws_idle) and
 *   checks once a second whether any open socket with subscriptions has gone silent for wsStaleMs. A pong counts as a
 *   frame, so a user socket with quiet streams, pinging every 30 s, is not silent.
 * - Hidden tabs (#1809, W12): timers there run late or not at all, so what they measure says nothing about Hyperliquid. No
 *   silence is judged while hidden, and on return (noteReturn) the wait starts again. An /info attempt started before the
 *   return, or ending while hidden, is not reported, so a frozen request's timeout or error is not a false outage.
 * - A 1 s tick drives the time-based rules. A probe (POST /info {"type":"exchangeStatus"}, a 45-byte answer) is sent
 *   only while the tab is visible and only when no /info attempt finished for probeIdleMs (probeUnhealthyMs while
 *   degraded / down). No third-party status service, no backend.
 * - Cooldown (#1809): the monitor owns the network's InfoCooldown because it outlives the InfoClient (env switches).
 *   A cooldown is our own rate limit, not an outage: it never feeds the machine, so it cannot reach `down` or gate
 *   orders. 429s stay `info_slow`; nothing is reported while requests wait, and no probe is sent. visibleStatus shows
 *   it as degraded (amber, "prices may be stale") because reads are paused even after a single 429.
 */
import { ENVIRONMENTS, type EnvConfig, type EnvName } from '../config/env';
import {
  DEFAULT_HEALTH_THRESHOLDS,
  INITIAL_HEALTH,
  reduceHealth,
  type HealthEvent,
  type HealthState,
  type HealthStatus,
  type HealthThresholds,
} from './health';
import { isAbortError } from './info';
import { InfoCooldown } from './infoCooldown';
import type { WsState } from './ws';
import type { WsSockets } from './wsSockets';

/** Distributes Omit over the event union so each variant keeps its own fields. */
type Untimed<E> = E extends { t: number } ? Omit<E, 't'> : never;
export type HealthInput = Untimed<HealthEvent>;

/** Test seam (e2e): `window.__hip4HealthThresholds = { recoverMs: 1500, … }` before the app loads. */
const OVERRIDE_KEY = '__hip4HealthThresholds';

export interface HealthMonitorOptions {
  thresholds?: Partial<HealthThresholds>;
  now?: () => number;
  fetch?: typeof fetch;
  tickMs?: number;
  isVisible?: () => boolean;
}

function windowOverrides(): Partial<HealthThresholds> {
  try {
    const o = (globalThis as Record<string, unknown>)[OVERRIDE_KEY];
    return o && typeof o === 'object' ? (o as Partial<HealthThresholds>) : {};
  } catch {
    return {};
  }
}

const defaultVisible = () => typeof document === 'undefined' || document.visibilityState === 'visible';

export class HealthMonitor {
  readonly thresholds: HealthThresholds;
  readonly cooldown: InfoCooldown;
  private st: HealthState = { ...INITIAL_HEALTH };
  private listeners = new Set<() => void>();
  private readonly now: () => number;
  private readonly fetchImpl: typeof fetch;
  private readonly tickMs: number;
  private readonly isVisible: () => boolean;
  private starts = 0;
  private timer: ReturnType<typeof setInterval> | null = null;
  private lastInfoAt = 0;
  /** The last time the tab came back. */
  private returnedAt = 0;
  private probing = false;
  private sockets: WsSockets | null = null;
  private stale = false;
  private cooling = false;

  constructor(
    readonly env: Pick<EnvConfig, 'name' | 'infoUrl'>,
    opts: HealthMonitorOptions = {},
  ) {
    this.thresholds = { ...DEFAULT_HEALTH_THRESHOLDS, ...windowOverrides(), ...opts.thresholds };
    this.now = opts.now ?? Date.now;
    this.fetchImpl = opts.fetch ?? ((...a: Parameters<typeof fetch>) => globalThis.fetch(...a));
    this.tickMs = opts.tickMs ?? 1_000;
    this.isVisible = opts.isVisible ?? defaultVisible;
    this.lastInfoAt = this.now();
    this.cooldown = new InfoCooldown({ now: this.now, onHit: () => this.syncCooldown() });
  }

  get status(): HealthStatus {
    return this.st.status;
  }

  /** What the user sees: `status`, or degraded while /info reads are paused by the cooldown. */
  get visibleStatus(): HealthStatus {
    return this.st.status === 'ok' && this.cooling ? 'degraded' : this.st.status;
  }

  get state(): Readonly<HealthState> {
    return this.st;
  }

  /** Called when the status or visibleStatus changes. */
  subscribe(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  report(input: HealthInput): void {
    const prev = this.st.status;
    this.st = reduceHealth(this.st, { ...input, t: this.now() } as HealthEvent, this.thresholds);
    if (input.type.startsWith('info_')) this.lastInfoAt = this.now();
    if (this.st.status !== prev) for (const cb of [...this.listeners]) cb();
  }

  /** Back to the initial (ok) state, e.g. when this network becomes the selected one again. */
  reset(): void {
    const prev = this.st.status;
    this.st = { ...INITIAL_HEALTH };
    this.stale = false;
    this.lastInfoAt = this.now();
    if (prev !== 'ok') for (const cb of [...this.listeners]) cb();
  }

  private syncCooldown(): void {
    const cooling = this.cooldown.active;
    if (cooling === this.cooling) return;
    this.cooling = cooling;
    for (const cb of [...this.listeners]) cb();
  }

  /** The tab is back from being hidden: time spent away is not silence, and a probe need not go at once. */
  noteReturn(): void {
    this.returnedAt = this.lastInfoAt = this.now();
  }

  /** Wrap the fetch used for /info so every attempt is reported. */
  wrapFetch(base: typeof fetch): typeof fetch {
    return async (input, init) => {
      const started = this.now();
      let timedOut = false;
      const reportable = () => !timedOut && this.isVisible() && started >= this.returnedAt;
      const timer = setTimeout(() => {
        if (reportable()) this.report({ type: 'info_timeout' });
        timedOut = true;
      }, this.thresholds.infoTimeoutMs);
      try {
        const res = await base(input, init);
        clearTimeout(timer);
        if (reportable()) {
          if (res.status === 429) this.report({ type: 'info_slow' });
          else if (res.status >= 500) this.report({ type: 'info_error' });
          else this.report({ type: 'info_ok', latencyMs: this.now() - started });
        }
        return res;
      } catch (e) {
        clearTimeout(timer);
        if (reportable() && !isAbortError(e) && !init?.signal?.aborted) this.report({ type: 'info_error' });
        throw e;
      }
    };
  }

  /** Follow the sockets' connection state and staleness. Returns a detach function. */
  attachWs(sockets: WsSockets): () => void {
    this.sockets = sockets;
    this.stale = false;
    const onState = (s: WsState) => {
      if (s === 'open') this.report({ type: 'ws_up' });
      else if (s === 'reconnecting') this.report({ type: 'ws_down' });
      else if (s === 'closed') this.report({ type: 'ws_idle' });
    };
    onState(sockets.state);
    const off = sockets.onStateChange(onState);
    return () => {
      off();
      if (this.sockets === sockets) this.sockets = null;
    };
  }

  /** Start the 1 s tick (ref-counted). Returns stop. */
  start(): () => void {
    this.starts++;
    if (!this.timer) this.timer = setInterval(() => this.tick(), this.tickMs);
    let stopped = false;
    return () => {
      if (stopped) return;
      stopped = true;
      this.starts--;
      if (this.starts === 0 && this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    };
  }

  /** One tick: staleness, time-based rules, maybe a probe. Public for tests. */
  tick(): void {
    const now = this.now();
    this.syncCooldown();
    if (this.sockets) {
      const quietFor = (at: number) => now - Math.max(at, this.returnedAt);
      const silent =
        this.isVisible() &&
        this.sockets.all.some(
          (ws) => ws.state === 'open' && ws.subscriptionCount > 0 && quietFor(ws.lastFrameAt) >= this.thresholds.wsStaleMs,
        );
      if (silent && !this.stale) {
        this.stale = true;
        this.report({ type: 'ws_stale' });
      } else if (!silent && this.stale) {
        this.stale = false;
        this.report({ type: 'ws_fresh' });
      }
    }
    this.report({ type: 'tick' });
    const every = this.st.status === 'ok' ? this.thresholds.probeIdleMs : this.thresholds.probeUnhealthyMs;
    if (!this.probing && !this.cooling && now - this.lastInfoAt >= every && this.isVisible()) void this.probe();
  }

  private async probe(): Promise<void> {
    this.probing = true;
    try {
      const res = await this.cooldown.wrapFetch(this.wrapFetch(this.fetchImpl))(this.env.infoUrl, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ type: 'exchangeStatus' }),
      });
      await res.text().catch(() => '');
    } catch {
      /* reported by wrapFetch */
    } finally {
      this.probing = false;
    }
  }
}

const registry = new Map<EnvName, HealthMonitor>();

/** The process-wide monitor for one network. */
export function healthMonitor(name: EnvName): HealthMonitor {
  let m = registry.get(name);
  if (!m) {
    m = new HealthMonitor(ENVIRONMENTS[name]);
    registry.set(name, m);
  }
  return m;
}

/** Monitor whose network owns this /exchange or /info URL (null for any other URL, e.g. in unit tests). */
export function healthMonitorForUrl(url: string): HealthMonitor | null {
  for (const env of Object.values(ENVIRONMENTS)) {
    if (url === env.exchangeUrl || url === env.infoUrl) return healthMonitor(env.name);
  }
  return null;
}

/** `base` behind the cooldown of the network owning this /info URL, for /info reads made outside InfoClient. */
export function gatedInfoFetch(url: string, base: typeof fetch): typeof fetch {
  return healthMonitorForUrl(url)?.cooldown.wrapFetch(base) ?? base;
}

/** /exchange transport outcome. `ok` = an HTTP answer below 500 (order rejections included). */
export function reportExchangeTransport(url: string, ok: boolean): void {
  healthMonitorForUrl(url)?.report({ type: ok ? 'exchange_ok' : 'exchange_error' });
}

/** Unit tests only: forget every monitor. */
export function resetHealthRegistryForTests(): void {
  registry.clear();
}
